import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import api from '../api';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  // 2FA state
  const [twoFAState, setTwoFAState] = useState(null); // {userId, email}
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const codeRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  const { login, verify2FA } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get('/auth/needs-setup').then(r => {
      if (r.data.needsSetup) nav('/setup');
      else setChecking(false);
    }).catch(() => setChecking(false));
  }, [nav]);

  const handleSubmit = async e => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.requires2FA) {
        setTwoFAState({ userId: result.userId, email: result.email });
        setTimeout(() => codeRefs[0].current?.focus(), 100);
      } else {
        nav('/');
      }
    } catch (err) { setError(err.response?.data?.error || 'Błąd logowania'); }
    finally { setLoading(false); }
  };

  const handleCodeInput = (i, value) => {
    const v = value.replace(/\D/g, '').slice(-1);
    const newCode = [...code];
    newCode[i] = v;
    setCode(newCode);
    if (v && i < 5) codeRefs[i + 1].current?.focus();
    if (newCode.every(d => d !== '')) {
      handleVerify(newCode.join(''));
    }
  };

  const handleCodeKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !code[i] && i > 0) {
      codeRefs[i - 1].current?.focus();
    }
  };

  const handleCodePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newCode = pasted.split('');
      setCode(newCode);
      codeRefs[5].current?.focus();
      handleVerify(pasted);
    }
  };

  const handleVerify = async (fullCode) => {
    setVerifying(true); setError('');
    try {
      await verify2FA(twoFAState.userId, fullCode);
      nav('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Błąd weryfikacji');
      setCode(['', '', '', '', '', '']);
      setTimeout(() => codeRefs[0].current?.focus(), 100);
    } finally { setVerifying(false); }
  };

  if (checking) return null;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--gray-50)' }}>
      <div style={{ width: '100%', maxWidth: 400, padding: '0 16px' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📚</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--gray-900)' }}>Baza Wiedzy</h1>
          <p style={{ color: 'var(--gray-500)', marginTop: 4 }}>
            {twoFAState ? 'Weryfikacja dwuetapowa' : 'Zaloguj się do systemu'}
          </p>
        </div>

        <div className="card" style={{ padding: '28px 32px' }}>
          {!twoFAState ? (
            // STEP 1: email + password
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">Hasło</label>
                <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
              </div>
              {error && <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{error}</div>}
              <button className="btn btn-primary" type="submit" disabled={loading} style={{ width: '100%', justifyContent: 'center', padding: '10px' }}>
                {loading ? 'Sprawdzam...' : 'Dalej →'}
              </button>
            </form>
          ) : (
            // STEP 2: 6-digit code
            <div>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 40, marginBottom: 8 }}>📧</div>
                <p style={{ fontSize: 14, color: 'var(--gray-600)' }}>
                  Wysłaliśmy 6-cyfrowy kod na adres<br />
                  <strong>{twoFAState.email}</strong>
                </p>
                <p className="text-muted" style={{ marginTop: 6 }}>Kod jest ważny przez 10 minut</p>
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
                {code.map((digit, i) => (
                  <input
                    key={i}
                    ref={codeRefs[i]}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleCodeInput(i, e.target.value)}
                    onKeyDown={e => handleCodeKeyDown(i, e)}
                    onPaste={handleCodePaste}
                    style={{
                      width: 48, height: 56, textAlign: 'center', fontSize: 24, fontWeight: 700,
                      border: `2px solid ${digit ? 'var(--primary)' : 'var(--gray-300)'}`,
                      borderRadius: 8, outline: 'none', background: digit ? 'var(--primary-light)' : 'white',
                      transition: 'all .1s'
                    }}
                  />
                ))}
              </div>

              {error && <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12, textAlign: 'center' }}>{error}</div>}

              {verifying && (
                <div style={{ textAlign: 'center', color: 'var(--gray-400)', fontSize: 13 }}>
                  <div className="spinner" style={{ margin: '0 auto 8px' }}></div>
                  Weryfikuję...
                </div>
              )}

              <button
                className="btn btn-secondary"
                style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
                onClick={() => { setTwoFAState(null); setCode(['','','','','','']); setError(''); }}
              >
                ← Wróć do logowania
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function SetupPage() {
  const [form, setForm] = useState({ email: '', name: '', password: '' });
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get('/auth/needs-setup').then(r => {
      if (!r.data.needsSetup) nav('/login');
      else setChecking(false);
    }).catch(() => setChecking(false));
  }, [nav]);

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      await api.post('/auth/setup', form);
      setDone(true);
      setTimeout(() => nav('/login'), 1800);
    } catch (err) { setError(err.response?.data?.error || 'Błąd'); }
  };

  if (checking) return null;

  if (done) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
        <p>Konto admina utworzone. Przekierowuję do logowania...</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--gray-50)' }}>
      <div style={{ width: '100%', maxWidth: 400, padding: '0 16px' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>⚙️</div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>Konfiguracja systemu</h1>
          <p style={{ color: 'var(--gray-500)', marginTop: 4, fontSize: 13 }}>Utwórz pierwsze konto administratora</p>
        </div>
        <div className="card" style={{ padding: '28px 32px' }}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Imię i nazwisko</label>
              <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label className="form-label">Hasło</label>
              <input className="form-input" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required minLength={6} />
            </div>
            {error && <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{error}</div>}
            <button className="btn btn-primary" type="submit" style={{ width: '100%', justifyContent: 'center', padding: '10px' }}>
              Utwórz konto admina
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
